import data from "../../shared/sharedData";

export default {
  name: "Appendix A - Employer Client Application",
  pageInfo: {
    text: `<p><i><b><u>Important Note:</u></b>  Employers are required to complete Appendix A when supplying information about the employer-client and its point of contact. Submission of this information in any other formor format will not be accepted and will result in the application being rejected for processing by the Department.</i></p>`,
    coloring: `grey`,
    isVisible: {
      when: "b14_emp_type_id",
      is: ["Job Contractor - Joint Employer"],
      operator: "compareMultiFieldValuesAND_ORInArray",
    },
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify in Field B.14 whether you are filing as an individual employer or a Job Contractor - Joint Employer.",
        type: "info",
        isVisible: {
          when: "b14_emp_type_id",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you are filing as an Individual Employer per Field B.14, no Appendix A is necessary for this application.",
        type: "success",
        isVisible: {
          when: "b14_emp_type_id",
          is: "Individual Employer",
        },
      },
    ],
  },
  sections: [
    {
      name: "Employer-Client Name(s)",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          machine_name: "appx_a_emp_name",
          widget: "text",
          code: "1",
          label: "Legal Business Name",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full Employer Name.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the full name of the individual employer-client. The employer-client’s full legal business name is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the IRS.",
        },
        {
          machine_name: "appx_a_emp_dba",
          code: "2",
          widget: "text",
          label: "Trade name/Doing Business As (DBA), if applicable",
          maxLength: 66,
          tooltip:
            "Enter the full trade name or “Doing Business As” (DBA) name of the employer-client, if applicable. Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Employer-Client Address",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          machine_name: "appx_a_emp_address_1",
          widget: "text",
          code: "3",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
            "noNAs",
          ],
          maxLength: 60,
          tooltip:
            "Enter the street address of the employer-client’s principal place of business. The address must be a physical location and not a P.O. Box.",
        },
        {
          machine_name: "appx_a_emp_address_2",
          code: "4",
          widget: "text",
          label: `Address 2 <i>(apartment/suite/floor and number)</i>`,
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this field to complete the employer-client’s street address. If no additional space is needed, enter “N/A.”`,
        },
        {
          code: "5",
          label: "Country",
          machine_name: "appx_a_emp_country_id",
          widget: "select",
          selection: data.country,
          clearSchema: ["appx_a_emp_state_id", "appx_a_emp_zip"],
          validate: ["required"],
          tooltip: `Enter the country of the employer-client’s principal place of business.`,
        },
        {
          machine_name: "appx_a_emp_city",
          code: "6",
          widget: "text",
          disabled: true,
          disableWhenField: "appx_a_emp_country_id",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          label: `City`,
          maxLength: 35,
          tooltip: `Enter the city of the employer-client’s principal place of business.`,
        },
        {
          machine_name: "appx_a_emp_state_id",
          code: "7",
          widget: "select",
          selection: data.state,
          state: true,
          disabled: true,
          disableWhenField: "appx_a_emp_country_id",
          isVisible: {
            when: "appx_a_emp_country_id",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            "required",
            {
              isRequired: "appx_a_emp_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `State`,
          maxLength: 35,
          tooltip: `Enter the State, District, or Territory of the employer-client’s principal place of business.`,
        },
        {
          machine_name: "appx_a_emp_zip",
          code: "8",
          widget: "text",
          disabled: true,
          disableWhenField: "appx_a_emp_country_id",
          validate: [
            "required",
            {
              postalCountry: "appx_a_emp_country_id",
              needsFieldValues: true,
              extraData: {
                valueOf: "appx_a_emp_country_id",
              },
            },
            {
              isRequired: "appx_a_emp_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          label: `Postal code`,
          maxLength: 10,
          tooltip: `Enter the postal (zip) code of the employer-client’s principal place of business.`,
        },
        {
          code: "9",
          label: "Province",
          machine_name: "appx_a_emp_province",
          widget: "text",
          disabled: true,
          disableWhenField: "appx_a_emp_country_id",
          validate: [
            {
              isRequired: "appx_a_emp_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip: `Enter the province of the employer-client’s principal place of business, if applicable. Enter “N/A” if not applicable.`,
          maxLength: 25,
        },
      ],
    },
    {
      name: "Employer-Client Contact Information",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          code: "10",
          machine_name: "appx_a_emp_phone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and telephone number for the employer-client’s principal place of business. Include country code, if outside of the United States.",
        },
        {
          code: "11",
          machine_name: "appx_a_emp_phone_ext",
          widget: "text",
          label: "Extension",
          maxLength: 4,
          validate: ["number"],
          parse: "positiveWholeNumber",
          tooltip: `Enter the extension of the telephone number for the employer-client’s principal place of business, if applicable. Enter “N/A” if not applicable.`,
        },
      ],
    },
    {
      name: "Employer-Client Identifiers",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          code: "12",
          machine_name: "appx_a_emp_fein",
          widget: "text",
          label: "Federal Employer Identification Number (FEIN from IRS)",
          validate: [
            "required",
            "fein",
            { minLength: 10, message: "This is not a valid FEIN" },
            {
              FEINPrefixValidator: "appx_a_emp_fein",
              needsFieldValues: true,
              message: "This is not a valid FEIN",
            },
          ],
          parse: "fein",
          maxLength: 10,
          tooltip: `Enter the nine-digit Federal Employer identification Number (FEIN) as assigned by the IRS.  Do not enter a social security number. Note: All employers, including private households, MUST obtain an FEIN from the IRS before completing this application.  Information on obtaining an FEIN can be found at www.irs.gov.`,
        },
        {
          code: "13",
          machine_name: "appx_a_emp_naics",
          field_to_modify: "appx_a_emp_naics_description",
          widget: "naics",
          label: "NAICS Code",
          validate: ["required"],
          tooltip: `Enter the four-to-six digit North American Industry Classification System (NAICS) code that best describes the employer’s business, not the foreign national’s job classification. A listing of NAICS codes can be found at <a href="http://www.census.gov/eos/www/naics">http://www.census.gov/eos/www/naics</a>.`,
        },
      ],
    },
    {
      name: "Employer-Client Point of Contact Name & Title",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          code: "1",
          machine_name: "appx_a_poc_last",
          widget: "text",
          label: "Contact's Last (family) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Contact's full Last Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the last (family) name of the employer-client’s point of contact.",
        },
        {
          code: "2",
          machine_name: "appx_a_poc_first",
          widget: "text",
          label: "First (given) Name",
          validate: [
            "required",
            {
              minLength: 2,
              message:
                "Contact's full First Name must contain at least 2 characters.",
            },
          ],
          maxLength: 35,
          tooltip:
            "Enter the first (given) name of the employer-client’s point of contact.",
        },
        {
          code: "3",
          machine_name: "appx_a_poc_middle",
          widget: "text",
          label: "Middle name(s)",
          maxLength: 35,
          tooltip:
            "Enter the middle name(s) of the employer-client’s point of contact, if applicable. Enter “N/A” if not applicable.",
        },
        {
          code: "4",
          machine_name: "appx_a_poc_title",
          widget: "text",
          label: "Contact's Job Title",
          validate: [
            "required",
            {
              minLength: 5,
              message:
                "Contact's full Job Title must contain at least 5 characters.",
            },
          ],
          maxLength: 60,
          tooltip:
            "Enter the job title of the employer-client’s point of contact.",
        },
      ],
    },
    {
      name: "Employer-Client Point of Contact Address",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          code: "5",
          machine_name: "appx_a_poc_address_1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
          ],
          maxLength: 60,
          tooltip: `Enter the business street address for the employer-client’s point of contact. The address must be a physical location and not a P.O. Box.`,
        },
        {
          code: "6",
          machine_name: "appx_a_poc_address_2",
          widget: "text",
          label:
            "<span>Address 2 <i>(apartment/suite/floor and number)</i></span>",
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this field to complete the street address. If no additional space is needed, enter “N/A.”`,
        },
        {
          code: "7",
          machine_name: "appx_a_poc_country_id",
          widget: "select",
          label: "Country",
          clearSchema: ["appx_a_poc_state_id", "appx_a_poc_zip"],
          selection: data.country,
          validate: ["required"],
          tooltip:
            "Enter the country of the employer-client’s point of contact.",
        },
        {
          code: "8",
          machine_name: "appx_a_poc_city",
          widget: "text",
          label: "City",
          disabled: true,
          disableWhenField: "appx_a_poc_country_id",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
          ],
          maxLength: 35,
          tooltip: "Enter the city of the employer-client’s point of contact.",
        },
        {
          code: "9",
          machine_name: "appx_a_poc_state_id",
          widget: "select",
          label: "State",
          disabled: true,
          disableWhenField: "appx_a_poc_country_id",
          selection: data.state,
          state: true,
          isVisible: {
            when: "appx_a_poc_country_id",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
          },
          validate: ["required"],
          tooltip:
            "Enter the State, District, or Territory of the employer-client’s point of contact.",
        },
        {
          code: "10",
          machine_name: "appx_a_poc_zip",
          widget: "text",
          disabled: true,
          disableWhenField: "appx_a_poc_country_id",
          label: "Postal Code",
          validate: [
            "required",
            {
              postalCountry: "appx_a_poc_country_id",
              needsFieldValues: true,
              extraData: {
                valueOf: "appx_a_poc_country_id",
              },
            },
            {
              isRequired: "appx_a_poc_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],

          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code of the employer-client’s point of contact.",
        },
        {
          code: "11",
          machine_name: "appx_a_poc_province",
          widget: "text",
          disabled: true,
          disableWhenField: "appx_a_poc_country_id",
          label: "Province",
          maxLength: 25,
          validate: [
            {
              isRequired: "appx_a_poc_country_id",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip:
            "Enter the province of the employer-client’s point of contact, if applicable. Enter “N/A” if not applicable.",
        },
      ],
    },
    {
      name: "Employer-Client Point of Contact - Contact Information",
      isVisible: {
        when: "b14_emp_type_id",
        is: "Job Contractor - Joint Employer",
      },
      fields: [
        {
          code: "12",
          machine_name: "appx_a_poc_phone",
          widget: "phone",
          label: "Telephone Number",
          validate: ["required", "phone"],
          tooltip:
            "Enter the area code and business telephone number of the employer-client’s point of contact. Include country code, if the point of contact is located outside of the United States.",
        },
        {
          code: "13",
          machine_name: "appx_a_poc_phone_ext",
          widget: "text",
          label: "Extension",
          validate: ["number"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the extension of the telephone number of the employer-client’s point of contact, if applicable. Enter “N/A” if not applicable.",
        },
        {
          code: "14",
          machine_name: "appx_a_poc_email",
          widget: "text",
          label: "Business e-mail address",
          validate: [
            "required",
            {
              email: {
                extraData: {
                  allowNAs: true,
                },
              },
            },
          ],
          maxLength: 100,
          tooltip: `Enter the business email address of the employer-client’s point of contact in the format name@emailaddress.top-level domain. The email entered in this field must be the same as the one regularly used by the employer-client’s point of contact for its business operations and capable of sending and receiving electronic communications from the Department with respect to the processing of this application. If the employer-client’s point of contact does not possess a business email address, please enter “N/A.”`,
        },
      ],
    },
  ],
};
